import { EditorView } from 'prosemirror-view'

type AsteriskPosition = {
  docYOffset: number
  blockId: string
  blockYOffset: number
}

type AsteriskPositionData = {
  [docYOffset: number]: Omit<AsteriskPosition, 'docYOffset'> & {
    type: 'changed' | 'removed'
  }
}

const processOneChange = (params: {
  editorView: EditorView
  position: number
  type: 'changed' | 'removed'
}): AsteriskPosition | undefined => {
  const { editorView, position, type } = params
  const node = editorView.state.doc.nodeAt(position)

  // we only want to add asterisks that are in text nodes
  if (node && node.isText) {
    // use prosemirror resolve to find the parent block of depth 2
    // (which will have an ID)
    const resolved = editorView.state.doc.resolve(position)
    const block = resolved.node(2)
    const blockId = block.attrs.id
    // Use the block's coords and the change's coords to figure out
    // how far from the top of the block the asterisk needs to go
    const blockStart = resolved.start(2)
    const { top } = editorView.coordsAtPos(position)
    const blockTop = editorView.coordsAtPos(blockStart).top
    const blockYOffset = top - blockTop

    return {
      docYOffset: top,
      blockId,
      blockYOffset,
    }

    // for removed nodes, if we aren't in a text node we want to find the last text node BEFORE
    // the removal and use that for the mark. So, if we're not at 0, recurse with the previous
    // position
  } else if (type === 'removed') {
    if (position > 0) {
      return processOneChange({ editorView, type, position: position - 1 })
    }

    // It's possible to remove text from the beginning of the document We want
    // to mark the deletion at the top of the first block in the document:
    const firstBlock = editorView.state.doc.firstChild?.firstChild
    if (!firstBlock) return

    return {
      docYOffset: 0,
      blockId: firstBlock.attrs.id,
      blockYOffset: 0,
    }
  }
}

export const buildAsteriskData = (params: {
  editorView: EditorView
  changes: [number, number][]
  removals: number[]
}): AsteriskPositionData => {
  const { editorView, changes, removals } = params
  // create a map where the keys are the "top" DOM position and the values
  // give us the needed info to put a symbol at the correct place in the doc.
  // By doing this as a map, we can consolidate multiple changes that affect the same
  // line (and clobber deletion marks with addition marks)
  const asteriskMap: AsteriskPositionData = {}

  // start by processing the removals as we want them to get clobbered
  removals.forEach((position) => {
    const result = processOneChange({ editorView, position, type: 'removed' })
    if (result) {
      asteriskMap[result.docYOffset] = {
        type: 'removed',
        blockId: result.blockId,
        blockYOffset: result.blockYOffset,
      }
    }
  })

  // now process the changes, additions. We want to process each individual character in
  // the range
  changes.forEach(([startPosition, endPosition]) => {
    for (let i = startPosition; i < endPosition; i++) {
      const result = processOneChange({
        editorView,
        position: i,
        type: 'changed',
      })
      if (result) {
        asteriskMap[result.docYOffset] = {
          type: 'changed',
          blockId: result.blockId,
          blockYOffset: result.blockYOffset,
        }
      }
    }
  })

  return asteriskMap
}

export const insertAsteriskNodes = (
  data: AsteriskPositionData,
  useRightSideMarks = false,
) => {
  // the X position of the revision asterisk is absolute, based on the
  // right edge of the editor. Note that although this seems overly ornate,
  // the server lays out the dom differently and changes to this positioning
  // model are fragile
  const editor = document.getElementById('prosemirror-editor')
  const horizontalPosition = editor
    ? editor.getBoundingClientRect().right - 96
    : 0

  Object.values(data).forEach(({ blockId, blockYOffset, type }) => {
    const block = document.getElementById(blockId)
    if (!block || block.textContent?.length === 0) {
      return
    }

    // we're going to position the attribute relative to the block, so it needs
    // to have a position attributed
    const isAddition = type === 'changed'
    const revType = isAddition ? 'revised' : 'removed'
    // create a span with + or - and give the appropriate classes
    const asterisk = document.createElement('span')
    asterisk.setAttribute('class', `o-revision o-revision__${revType}`)

    // positioning:
    //
    // For the top, we use the prosemirror-discovered offset from the parent
    // block.
    //
    // Horizontally, the asterisk is positioned relative to the block element:
    // - For the left gutter: negate the block's own margin-left to walk back to
    //   the page content edge, so all marks align regardless of block
    //   indentation at the page's left margin
    // - For the right gutter: use the viewport-based editor right edge minus
    //   the block's viewport left
    const parentLeft = block.getBoundingClientRect().left
    const blockMarginLeft = parseFloat(getComputedStyle(block).marginLeft)
    const asteriskLeft = useRightSideMarks
      ? horizontalPosition - parentLeft
      : -blockMarginLeft
    asterisk.setAttribute(
      'style',
      `top: ${blockYOffset}px;left: ${asteriskLeft}px;`,
    )
    block.appendChild(asterisk)
  })
}

export const removeAsteriskNodes = () => {
  const asterisks = document.querySelectorAll('.o-revision')
  asterisks.forEach((a) => a.remove())
}
